import { Components } from '@mui/material/styles';
import { CSSObject, Theme } from '@mui/material';
import { CloseIcon } from '@/src/components/icon/stroke/CloseIcon';
import { getFocusRingStyle, updateFocusRingStyle } from '@/src/components/focusring/focusring';
import { getInteractiveStyle } from '@/src/materialui/button/interactive';

/**
 * Wrapper around getInteractiveStyle() function to provide interactive style
 *  only for chip that is actually a button
 */
function getChipInteractiveStyle(defaultStyle: CSSObject, hoverStyle: CSSObject): CSSObject {
  return {
    ...defaultStyle,
    '&.MuiButtonBase-root': {
      ...getInteractiveStyle(defaultStyle, hoverStyle),
    },
  };
}

/**
 * Return style for either outlined or filled variant. Used by deprecated semantic colors
 */
function getVariantStyle(theme: Theme, variant: string, primaryColor: string, hover?: boolean): CSSObject {
  if (variant === 'outlined') {
    return {
      backgroundColor: hover ? theme.palette.transparent.secondary : 'transparent',
      color: primaryColor,
      borderColor: primaryColor,
    };
  } else {
    return {
      backgroundColor: primaryColor,
      color: theme.palette.onSurface.os0,
      borderColor: primaryColor,
    };
  }
}

export const flowChip: Components<Theme>['MuiChip'] = {
  defaultProps: {
    deleteIcon: <CloseIcon />,
  },
  styleOverrides: {
    root: ({ theme, ownerState }) => ({
      position: 'relative',
      // proportions
      height: 'auto',
      minWidth: '0',
      padding: '5px 16px',
      // not recommended small variant
      ...(ownerState.size === 'small' && {
        padding: '2px 12px',
      }),
      // custom font size
      textRendering: 'geometricPrecision',
      textTransform: 'none',
      fontSize: '16px',
      lineHeight: '20px',
      fontWeight: '400',
      letterSpacing: '0px',
      outline: 'none',
      // 50% border radius without skewing ratio
      borderRadius: '1000px',
      textAlign: 'center',
      transition:
        'transform 0.3s ease-out, color 0.3s ease-out, background-color 0.3s ease-out, border-color 0.3s ease-out, box-shadow 0.3s ease-out, opacity 0.3s ease-out',
      // correct cursor if the chip is clickable button
      '&&.MuiButtonBase-root': {
        cursor: 'pointer',
      },
      // more important selector to overwrite default @mui styling
      '&, &:active': {
        boxShadow: 'none',
        // drop shadow variant (+ old API support)
        ...((ownerState.raised || ownerState.className?.includes('.MuiChip-shadow')) && {
          boxShadow: theme.flowShadows.xs,
        }),
      },
      // style (default 'filled' (solid) variant)
      border: `1px solid ${theme.palette.onSurface.os900}`,
      ...getChipInteractiveStyle(
        {
          backgroundColor: theme.palette.surface.primary,
          color: theme.palette.text.primary,
          borderColor: theme.palette.onSurface.os500,
          // drop shadow variant (+ old API support)
          ...((ownerState.raised || ownerState.className?.includes('.MuiChip-shadow')) && {
            backgroundColor: theme.palette.background.primary,
          }),
        },
        {
          backgroundColor: theme.palette.surface.secondary,
          color: theme.palette.text.primary,
          borderColor: theme.palette.onSurface.os500,
          // drop shadow variant (+ old API support)
          ...((ownerState.raised || ownerState.className?.includes('.MuiChip-shadow')) && {
            backgroundColor: theme.palette.surface.primary,
          }),
        },
      ),
      // transparent variant
      ...(ownerState.variant === 'transparent' && {
        ...getChipInteractiveStyle(
          {
            backgroundColor: theme.palette.transparent.primary,
          },
          {
            backgroundColor: theme.palette.transparent.secondary,
          },
        ),
      }),
      // deprecated secondary color
      ...(ownerState.color === 'secondary' &&
        ownerState.variant !== 'outlined' && {
          ...getChipInteractiveStyle(
            {
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.common.white,
              borderColor: theme.palette.secondary.main,
            },
            {
              backgroundColor: theme.palette.secondary.dark,
              color: theme.palette.common.white,
              borderColor: theme.palette.secondary.dark,
            },
          ),
        }),
      // deprecated semantic colors, used by Production team
      ...(ownerState.color === 'error' && {
        ...getChipInteractiveStyle(
          {
            ...getVariantStyle(theme, ownerState.variant, theme.palette.error.main),
          },
          {
            ...getVariantStyle(theme, ownerState.variant, theme.palette.error.dark, true),
          },
        ),
      }),
      ...(ownerState.color === 'warning' && {
        ...getChipInteractiveStyle(
          {
            ...getVariantStyle(theme, ownerState.variant, theme.palette.warning.main),
          },
          {
            ...getVariantStyle(theme, ownerState.variant, theme.palette.warning.dark, true),
          },
        ),
      }),
      ...(ownerState.color === 'info' && {
        ...getChipInteractiveStyle(
          {
            ...getVariantStyle(theme, ownerState.variant, theme.palette.info.main),
          },
          {
            ...getVariantStyle(theme, ownerState.variant, theme.palette.info.dark, true),
          },
        ),
      }),
      // selected chip
      ...((ownerState.isSelected || ['success', 'primary'].includes(ownerState.color)) && {
        borderWidth: '2px',
        // remove 1px border for each side
        padding: '4px 15px',
        ...(ownerState.size === 'small' && {
          padding: '1px 11px',
        }),
        ...getChipInteractiveStyle(
          theme.palette.mode === 'dark'
            ? {
                backgroundColor: theme.palette.onSurface.os800,
                color: theme.palette.brand.secondary,
                borderColor: theme.palette.brand.secondary,
              }
            : {
                backgroundColor: theme.palette.brand.quarternary,
                color: theme.palette.brand.primary,
                borderColor: theme.palette.brand.primary,
              },
          theme.palette.mode === 'dark'
            ? {
                backgroundColor: theme.palette.onSurface.os800,
                color: theme.palette.brandHover.secondary,
                borderColor: theme.palette.brandHover.secondary,
              }
            : {
                backgroundColor: theme.palette.brand.quarternary,
                color: theme.palette.brandHover.primary,
                borderColor: theme.palette.brandHover.primary,
              },
        ),
      }),
      // disabled
      '&.Mui-disabled': {
        opacity: '1',
        userSelect: 'none',
        color: theme.palette.disabled.primary,
        backgroundColor: theme.palette.surface.primary,
        borderColor: theme.palette.disabled.primary,
        // transparent variant
        ...(ownerState.variant === 'transparent' && {
          backgroundColor: theme.palette.transparent.primary,
        }),
      },
      // focused style
      ...getFocusRingStyle(theme, true, 1),
      ...((ownerState.isSelected || ['success', 'primary'].includes(ownerState.color)) && {
        ...updateFocusRingStyle(2),
      }),
      // old api, counting dot
      '& .count': {
        ...(theme.palette.mode === 'dark'
          ? {
              backgroundColor: theme.palette.brand.secondary,
              color: theme.palette.onSurface.os900,
            }
          : {
              backgroundColor: theme.palette.brand.primary,
              color: theme.palette.common.white,
            }),
        minWidth: '20px',
        height: '20px',
        borderRadius: '20px',
        paddingLeft: '4px',
        paddingRight: '4px',
        textAlign: 'center',
        marginLeft: '10px',
        fontSize: '14px',
        lineHeight: '20px',
        fontWeight: '700',
        '& + .MuiChip-deleteIcon': {
          marginLeft: '5px',
        },
      },
      '& .chevron-down, & .chevron-up, & .MuiSvgIcon-root:last-child': {
        fontSize: '17px',
        // space between text and icon
        marginRight: '0',
        marginLeft: '10px',
      },
      '&.chevron-down.isOpen, & .MuiSvgIcon-root:last-child.isOpen': {
        transform: 'rotate(180deg)',
      },
    }),
    icon: {
      '&, &:hover': {
        color: 'currentColor',
      },
      fontSize: '17px',
      // space between text and icon
      marginRight: '10px',
      marginLeft: '0',
    },
    deleteIcon: {
      '&, &:hover': {
        color: 'currentColor',
      },
      fontSize: '17px',
      // space between text and icon
      marginRight: '0',
      marginLeft: '10px',
    },
    label: {
      // has children
      '&:has(> *)': {
        // this breaks ellipsis, eases align of multiselection
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        // reactive ellipsis
        '& > div, & > span': {
          minWidth: '0',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        },
      },
      padding: '0',
    },
  },
};
